<template lang="pug">
#receive-message-setting(v-cloak)
  .box
    .box-header.with-border.register-header
      .title 受信メッセージの設定

    .box-body.register-body
      .function-list.d-flex.flex-column.align-items-center.mb-4
        .content.d-flex.flex-row.align-items-center(v-if="item")
          .icon.d-flex.flex-column.align-items-center
            img(:src="item.iconUrl")
            span.used(v-if="item.permitted == true") 使用中
            span.unused(v-else) 未使用
          .function.flex-grow-1
            .title {{ item.title }}
            .text {{ item.text }}
          .buttons.d-flex.flex-row-reverse
            PermittedFunctionSwitch(v-model="item.updatedPermitted")

      FormTextarea(key-name="入所申込者向け案内文" name="receive-message-guidance-to-waiters" v-model="receiveMessageGuidanceToWaiters"
        placeholder="例：施設への連絡時にお使いください。ただし返事に時間がかかることもありますので、お急ぎの場合はお電話でご連絡ください。"
        :validation-rules="{ max: 1000 }" :maxlength="1000" :styles="{ maxWidth: '640px', width: '640px', minHeight: '160px' }")
        template(#description)
          .mb-2 入所申込者が施設へのメッセージを入力するページに掲載する案内文を編集できます。

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoReceiveMessageList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="受信メッセージの設定を更新しますか？" @ok="updateSetting")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoReceiveMessageList")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import FacilitiesService, { FacilityGetModel, FacilityPatchModel, PermittedFunctions } from "@/commons/services/Facilities";
import { PlanType } from "@/commons/services/Plans";

import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import PermittedFunctionSwitch from "@/pc/basics/PermittedFunctionSwitch.vue";

/**
 * ### 受信メッセージの設定コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkModal,
    OkCancelModal,
    ValidationAlert,
    FormTextarea,
    PermittedFunctionSwitch
  },
  $_veeValidate: { validator: "new" }
})
export default class ReceiveMessageSetting extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** エラーメッセージ */
  errorMessage: string = "";

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 施設 */
  facility: FacilityGetModel | null = null;

  /** 受信メッセージ使用可否 */
  item: PermittedFunctionListItem | null = null;

  /** 受信メッセージ案内文 */
  receiveMessageGuidanceToWaiters: string = "";
  /** 更新日時 */
  updated: number = -1;

  /** ### プランがベーシック以上かを返す */
  get _isOverBasicPlan(): boolean {
    const planType: PlanType = userStore.facility.planType;

    if (PlanType.BASIC <= planType) {
      return true;
    } else {
      return false;
    }
  }

  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const facility: FacilityGetModel = userStore.getFacility;
    this.facility = facility;
    this.receiveMessageGuidanceToWaiters = facility.receiveMessageGuidanceToWaiters;
    this.updated = facility.updated;

    this.item = this.createItem(facility);

    this.isBusy = false;

    return;
  }

  public createItem(facility: FacilityGetModel): PermittedFunctionListItem {
    return {
      id: "opinion",
      permitted: facility.permittedFunctions.waiterOpinion,
      iconUrl: require("../../../assets/permitted-function-opinion-icon.png"),
      title: "入所申込者からの連絡",
      text: "入所申込者から施設に連絡・問い合わせが出来ます。施設では内容の確認と返信が出来ます。",
      linkUrl: "#waiterOpinion",
      updatedPermitted: facility.permittedFunctions.waiterOpinion
    };
  }

  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  public async updateSetting(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      if (this.facility && this.item) {
        const permittedFunctions: PermittedFunctions = {
          waiterOpinion: this.item.updatedPermitted,
          // 待機者の機能以外はここでは更新しない
          opinion: this.facility.permittedFunctions.opinion,
          reservation: this.facility.permittedFunctions.reservation,
          onlineReservation: this.facility.permittedFunctions.onlineReservation,
          outing: this.facility.permittedFunctions.outing,
          event: this.facility.permittedFunctions.event,
          careimage: this.facility.permittedFunctions.careimage,
          picture: this.facility.permittedFunctions.picture,
          otherUserOpinion: this.facility.permittedFunctions.otherUserOpinion
        };
        this.facility.permittedFunctions = permittedFunctions;

        const param: FacilityPatchModel = await this.createRequestBody(this.facility);
        await this.facilitiesService.patch(userStore, param);

        this.showUpdateSuccessModal = true;
      }
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public createRequestBody(facility: FacilityGetModel): FacilityPatchModel {
    return {
      address: facility.address,
      name: facility.name,
      tel: facility.tel,
      url: facility.url,
      permittedFunctions: facility.permittedFunctions,
      updated: this.updated,
      receiveMessageGuidanceToWaiters: this.receiveMessageGuidanceToWaiters
    };
  }

  public gotoReceiveMessageList(): void {
    this.$router.push({ name: PcUrl.WaiterReceiveMessageList });
    return;
  }
}

interface PermittedFunctionListItem {
  /** ページ内リンク用のID */
  id: string;
  /** 機能の使用可否 */
  permitted: boolean;
  /** アイコン画像URL */
  iconUrl: string;
  /** 機能名 */
  title: string;
  /** 機能説明文 */
  text: string;
  /** ページ内リンクURL */
  linkUrl: string;
  /** 変更後機能の使用可否 */
  updatedPermitted: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#receive-message-setting {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  // 使用機能の目次
  .function-list {
    width: 100%;

    .content {
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      // アイコン
      .icon {
        min-width: 60px;
        width: 60px;
        margin-right: 12px;
        // 使用中・未使用
        .used,
        .unused {
          font-weight: bold;
        }
        .used {
          color: $font-key-color;
        }
        .unused {
          color: $font-disable-font-color;
        }
      }
      // 機能名
      .function {
        margin-right: 12px;
        .title {
          font-size: 18px;
          font-weight: bold;
        }
      }
      // ON/OFFスイッチ
      .buttons {
        width: 130px;
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
